// src/components/ProfileEditSection.tsx
import React, { useState, useEffect, useContext } from 'react';
import { Edit, Save } from 'lucide-react';
import { useUserProfile } from '../../hooks/useUserProfile';
import { updateProfile } from '../../services/profileService';
import { UserContext } from '../../services/user/LoginUserContext';
import { logger } from '../../utils/logger';
import { Profile } from '../../types';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';


// Define the interface for the ProfileEditSection component's props
interface ProfileEditSectionProps {
  userId: string;
  handlePageChange: (page: string) => void;
  showMessage: (message: string, onConfirm: () => void, onCancel: null | (() => void), showCancel: boolean) => void;
}

const ProfileEditSection: React.FC<ProfileEditSectionProps> = ({ userId, handlePageChange, showMessage }) => {
  const { profile, isLoading, mutate: userProfileMutate }:
    {
      profile: Profile | null;
      isLoading: boolean;
      mutate: (data?: Profile | Promise<Profile> | null, shouldRevalidate?: boolean) => Promise<Profile | undefined>;
    } = useUserProfile(userId);
  const { user } = useContext(UserContext);

  const [newNickname, setNewNickname] = useState<string>('');
  const [newBirthday, setNewBirthday] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [hasShownInitialNicknamePrompt, setHasShownInitialNicknamePrompt] = useState(false);
  const isOnline = useOnlineStatus(); // オンライン状態を取得

  // 初回値セット
  useEffect(() => {
    if (profile) {
      logger.log('Profile updated:', profile);
      setNewNickname(profile.nickname || '');

      // 誕生日が設定されている場合のみ値をセット
      if (profile.birthday) {
        setNewBirthday(profile.birthday);
      } else {
        // 誕生日が未設定の場合、入力欄は空のままにする
        setNewBirthday('');
      }

      // Set initial email value if not already set
      if (user?.email && !profile.email) {
        setNewEmail(user.email);
      } else {
        setNewEmail(profile.email || '');
      }
      

      if (!profile.nickname && !hasShownInitialNicknamePrompt) {
        showMessage('ニックネームを入力してください。', () => { }, null, false);
        setIsEditing(true); // ニックネーム未設定なら自動的に編集モードにする
        setHasShownInitialNicknamePrompt(true); // メッセージを表示したことを記録
      }
    }
  }, [profile, showMessage, hasShownInitialNicknamePrompt]);

  const handleUpdateProfile = async () => {
    // オフライン時は処理を中断
    if (!isOnline) {
      showMessage('現在オフラインのため、プロフィールを更新できません。', () => { }, null, false);
      return;
    }

    if (!newNickname || newNickname.trim().length < 2 || newNickname.trim().length > 15) {
      showMessage('ニックネームは2文字以上15文字以内で入力してください。', () => { }, null, false);
      return;
    }

    // 誕生日が入力されている場合のみバリデーションを行う
    if (newBirthday) {
      const birthdayDate = new Date(newBirthday);
      const now = new Date();
      const hundredFiftyYearsAgo = new Date();
      hundredFiftyYearsAgo.setFullYear(now.getFullYear() - 150);

      if (birthdayDate > now) {
        showMessage('生年月日に未来の日付は設定できません。', () => { }, null, false);
        return;
      }
      if (birthdayDate < hundredFiftyYearsAgo) {
        showMessage('生年月日は150年以上前の日付は設定できません。', () => { }, null, false);
        return;
      }
    }

    const performUpdate = async () => {
      try {
        let birthdayToUpdate = newBirthday || null;
        
        let emailToUpdate = null;
        if (user?.email) {
          emailToUpdate = user.email;
        } else {
          emailToUpdate = (!profile?.email && newEmail) ? newEmail : null;
        }
        setNewEmail(emailToUpdate || '');

        await updateProfile(newNickname, birthdayToUpdate, userId,emailToUpdate);

        userProfileMutate();
        setIsEditing(false);
        showMessage('プロフィールを更新しました！', () => { }, null, false);

        if (!profile?.nickname && newNickname.trim()) {
          handlePageChange('home');
        }
      } catch (error) {
        logger.error('Update failed:', error);
        showMessage('更新に失敗しました。', () => { }, null, false);
      }
    };

    // 変更前の確認メッセージ
    const isNicknameChanged = newNickname !== profile?.nickname;
    const isBirthdayChanged = newBirthday !== profile?.birthday;
    const isFirstBirthdaySet = !profile?.birthday && !!newBirthday;

    // ニックネーム、または生年月日が変更された場合のみ確認メッセージを表示
    if (isNicknameChanged || isFirstBirthdaySet || isBirthdayChanged ) {
      const isFirstNicknameSet = !profile?.nickname && !!newNickname;
      let message = 'プロフィールを更新します。よろしいですか？';

      if (isFirstNicknameSet && isFirstBirthdaySet) {
        message = 'ニックネーム・生年月日を登録します。よろしいですか？';
      } else if (isFirstNicknameSet) {
        message = 'ニックネームを登録します。よろしいですか？';
      } else if (isFirstBirthdaySet) {
        message = '生年月日を登録します。よろしいですか？';
      }

      showMessage(message, performUpdate, () => { }, true);
    } else {
      // 変更がない場合は確認メッセージなしで更新を実行
      performUpdate();
    //      setIsEditing(false);
  }
  };

  const handleBirthdayFocus = () => {
    // 誕生日が未設定の場合のみ、15年前の今日をセット
    if (!newBirthday) {
      const fifteenYearsAgo = new Date();
      fifteenYearsAgo.setFullYear(fifteenYearsAgo.getFullYear() - 15);
      setNewBirthday(fifteenYearsAgo.toISOString().split('T')[0]);
    }
  };

  if (isLoading || !profile) return <p>読み込み中...</p>;

  const maxDate = new Date().toISOString().split('T')[0];
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 150);
  const minDateISO = minDate.toISOString().split('T')[0];

  return (
    <div className="bg-white p-6 rounded-2xl shadow-xl mb-8">
      <h2 className="text-2xl font-bold text-gray-700 mb-4">ユーザー情報</h2>
      {/* オフライン時にメッセージを表示 */}
      {!isOnline && (
        <div className="mb-6 p-4 rounded-lg font-semibold text-center bg-yellow-100 text-yellow-800 border border-yellow-200">
          <p>現在オフラインです。</p>
          <p>プロフィールの変更はオンライン時にご利用いただけます。</p>
        </div>
      )}
      <div className="space-y-3">
        {/* ニックネーム */}
        <div className="flex items-center">
          <strong className="w-28 text-gray-600">ニックネーム:</strong>
          {isEditing || !profile.nickname ? (
            <input
              value={newNickname}
              onChange={(e) => setNewNickname(e.target.value)}
              className={`p-2 border rounded-md ${!isOnline ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              disabled={!isOnline}
            />
          ) : (
            <span className="text-gray-800 font-semibold text-lg">{profile.nickname}</span>
          )}
        </div>

        {/* 生年月日 */}
        <div className="flex items-center">
          <strong className="w-28 text-gray-600">生年月日:</strong>
          {isEditing || !profile.nickname ? (
            <input
              type="date"
              value={newBirthday}
              onChange={(e) => setNewBirthday(e.target.value)}
              onFocus={handleBirthdayFocus}
              className={`p-2 border rounded-md ${!isOnline ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              disabled={!isOnline}
              max={maxDate}
              min={minDateISO}
            />
          ) : (
            <span className="text-gray-800 font-semibold text-lg">{profile.birthday || '未設定'}</span>
          )}
        </div>
        {/* Email */}
        <div className="flex items-center">
          <strong className="w-28 text-gray-600">e-mail:</strong>
          <span className="text-gray-800 font-semibold text-lg truncate">{newEmail || '未設定'}</span>
        </div>
        
        {/* ボタン */}
        <div className="flex justify-end pt-4 border-t mt-4">
          {isEditing || !profile.nickname ? (
            <div className="flex space-x-3">
              <button
                onClick={() => setIsEditing(false)}
                className="py-2 px-5 bg-gray-200 text-gray-700 font-semibold rounded-lg shadow hover:bg-gray-300 transition"
                disabled={!isOnline} // オフライン時は無効化
              >
                キャンセル
              </button>
              <button
                onClick={handleUpdateProfile}
                className={`
                  py-2 px-6 rounded-lg font-bold shadow-lg transform transition-all
                  ${!isOnline
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-custom-gradient from-sky-400 to-cyan-400 text-white hover:from-sky-500 hover:to-cyan-500 hover:scale-105'
                  }
                `}
                disabled={!isOnline} // オフライン時は無効化
              >
                <Save size={20} className="inline mr-2" /> 保存
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="py-2 px-5 bg-gray-200 text-gray-700 font-semibold rounded-lg shadow hover:bg-gray-300 transition"
              disabled={!isOnline} // オフライン時は無効化
            >
              <Edit size={18} className="inline mr-2" /> 編集
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileEditSection;